import { createContext, useContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';

// Create Theme Context
const ThemeContext = createContext(undefined);

// Custom hook to use theme context
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// ThemeProvider Component
export const ThemeProvider = ({ children, storageKey = 'oakley-theme-preference' }) => {
  // Initialize theme from localStorage or default to 'light'
  const [theme, setThemeState] = useState(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      return stored === 'dark' ? 'dark' : 'light';
    } catch (error) {
      console.warn('localStorage unavailable, using default theme:', error);
      return 'light'; // Fallback to default
    }
  });

  // Apply theme to <html> element on mount and theme change
  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }, [theme]);

  // Persist theme changes to localStorage
  const setTheme = (newTheme) => {
    if (newTheme !== 'light' && newTheme !== 'dark') {
      console.warn(`Invalid theme value: ${newTheme}. Using 'light' as fallback.`);
      newTheme = 'light';
    }
    
    setThemeState(newTheme);
    
    try {
      localStorage.setItem(storageKey, newTheme);
    } catch (error) {
      console.warn('Failed to persist theme preference:', error);
      // Continue without persistence - theme will work for current session
    }
  };

  // Toggle function for convenience
  const toggleTheme = () => {
    setTheme(theme === 'light' ? 'dark' : 'light');
  };

  const value = {
    theme,
    setTheme,
    toggleTheme,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

ThemeProvider.propTypes = {
  children: PropTypes.node.isRequired,
  storageKey: PropTypes.string,
};
